import React, { KeyboardEventHandler, createRef, useEffect, useState } from 'react';
import { ITextFieldProps, TextField } from './TextField';
import { DELIMITER, DateSegment } from './dateFieldUtils';

enum SpecialKeys {
  BACKSPACE = 'Backspace',
  DELETE = 'Delete',
  RIGHT = 'ArrowRight',
  LEFT = 'ArrowLeft'
}

export type DayMonthYear = {
  day?: number;
  month?: number;
  year?: number;
};

export interface IDateFieldProps extends Omit<ITextFieldProps, 'value' | 'onChange' | 'type' | 'placeholder'> {
  value?: DayMonthYear;
  onChange?: (value: DayMonthYear) => void;
}

const convertValueToSegments = (value: DayMonthYear | undefined): DateSegment[] =>
  value ? [DateSegment.day(value.day), DateSegment.month(value.month), DateSegment.year(value.year)] : DateSegment.defaultDateSegments();

export const DateField: React.FC<IDateFieldProps> = ({ value, onChange, ...rest }) => {
  const ref = createRef<HTMLInputElement>();
  const [positionIndex, setPositionIndex] = useState<number>(0); // 0, 1, 2 representing day, month, year
  const [dateValue, setDateValue] = useState<DateSegment[]>(convertValueToSegments(value));

  const setPosition = () => ref.current?.setSelectionRange(positionIndex * 3, positionIndex * 3 + (positionIndex === 2 ? 4 : 2));

  useEffect(() => {
    setPosition();
  }, [positionIndex, dateValue, ref]);

  useEffect(() => {
    setDateValue(convertValueToSegments(value));
    setPositionIndex(0);
  }, [value]);

  const decrementPositionIndex = () => setPositionIndex(Math.max(0, positionIndex - 1));
  const incrementPositionIndex = () => setPositionIndex(Math.min(2, positionIndex + 1));

  const handleOnKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === SpecialKeys.LEFT) {
      decrementPositionIndex();
    } else if (e.key === SpecialKeys.RIGHT) {
      incrementPositionIndex();
    } else if (/^\d+$/.test(e.key)) {
      const newDateValue = [...dateValue];
      const currentPart = newDateValue[positionIndex];
      currentPart.addDigit(Number(e.key));
      setDateValue(newDateValue);
      if (currentPart.isComplete()) incrementPositionIndex();
    } else if (e.key === SpecialKeys.BACKSPACE || e.key === SpecialKeys.DELETE) {
      const newDateValue = [...dateValue];
      const currentPart = newDateValue[positionIndex];
      if (currentPart.isEmpty()) decrementPositionIndex();
      currentPart.removeDigit();
      setDateValue(newDateValue);
    }

    e.preventDefault();
    e.stopPropagation();
    setPosition();

    onChange?.({
      day: dateValue[0].value(),
      month: dateValue[1].value(),
      year: dateValue[2].value()
    });
  };

  return (
    <TextField
      ref={ref}
      onKeyUp={handleOnKeyUp}
      onFocus={() => ref?.current?.setSelectionRange(0, 2)}
      {...rest}
      value={dateValue.map((s) => s.print()).join(DELIMITER)}
      onChange={() => {}}
    />
  );
};
